import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loginRequest, logoutRequest } from '../api/auth';
import { clearTokens, getRefreshToken, loadTokensFromStorage, setTokens } from '../api/client';

interface AuthContextValue {
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY_TOKEN = 'onlyseeker_admin_token';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    // Load tokens from storage on init
    return loadTokensFromStorage();
  });

  // Sync logout across tabs via storage event
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // If the token was removed in another tab, log out this tab too
      if (event.key === STORAGE_KEY_TOKEN && event.newValue === null) {
        setIsAuthenticated(false);
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
      // If token was added in another tab, sync auth state
      if (event.key === STORAGE_KEY_TOKEN && event.newValue !== null) {
        loadTokensFromStorage();
        setIsAuthenticated(true);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const tokenResponse = await loginRequest({ email, password });
    setTokens(tokenResponse);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    const currentRefreshToken = getRefreshToken();

    // Clear local state first
    clearTokens();
    setIsAuthenticated(false);

    // Try to invalidate refresh token on server (fire and forget)
    if (currentRefreshToken) {
      try {
        await logoutRequest(currentRefreshToken);
      } catch {
        // Ignore errors - we're logging out anyway
      }
    }

    window.location.href = '/login';
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      login,
      logout
    }),
    [isAuthenticated, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
